import React from 'react';
import { observer } from 'mobx-react';
import { ButtonClickEvent } from 'ui/base/types';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';
import { ProtocolFilter } from 'services/pangolin/types';
import { getProtocolFilterIcon, getProtocolFilterLabel } from './protocol_entry_logic';
import baseStyles from './protocol_entry.module.css';

type ProtocolEntryStyles = {
  plainContainer: string;
  clickableContainer: string;
  iconContainer: string;
  detailContainer: string;
  detailText: string;
  bareButton: string;
  removeIcon: string;

  // Filter-specific icons
  customIcon: string;
  hubIcon: string;
  lanIcon: string;
  mailIcon: string;
  shellIcon: string;
  storageIcon: string;
  timeIcon: string;
  webIcon: string;
};

const themedStyles: ThemedStyles<ProtocolEntryStyles> = {
  [Theme.DAYLIGHT]: {
    plainContainer: baseStyles.plainContainer,
    clickableContainer: baseStyles.clickableContainer,
    iconContainer: baseStyles.iconContainer,
    detailContainer: baseStyles.detailContainer,
    detailText: baseStyles.detailText,
    bareButton: baseStyles.bareButton,
    removeIcon: baseStyles.midnightRemoveIcon,

    // Filter-specific icons
    customIcon: baseStyles.daylightCustomIcon,
    hubIcon: baseStyles.daylightHubIcon,
    lanIcon: baseStyles.daylightLanIcon,
    mailIcon: baseStyles.daylightMailIcon,
    shellIcon: baseStyles.daylightShellIcon,
    storageIcon: baseStyles.daylightStorageIcon,
    timeIcon: baseStyles.daylightTimeIcon,
    webIcon: baseStyles.daylightWebIcon,
  },
  [Theme.MIDNIGHT]: {
    plainContainer: baseStyles.plainContainer,
    clickableContainer: baseStyles.clickableContainer,
    iconContainer: baseStyles.iconContainer,
    detailContainer: baseStyles.detailContainer,
    detailText: baseStyles.detailText,
    bareButton: baseStyles.bareButton,
    removeIcon: baseStyles.midnightRemoveIcon,

    // Filter-specific icons
    customIcon: baseStyles.midnightCustomIcon,
    hubIcon: baseStyles.midnightHubIcon,
    lanIcon: baseStyles.midnightLanIcon,
    mailIcon: baseStyles.midnightMailIcon,
    shellIcon: baseStyles.midnightShellIcon,
    storageIcon: baseStyles.midnightStorageIcon,
    timeIcon: baseStyles.midnightTimeIcon,
    webIcon: baseStyles.midnightWebIcon,
  },
};

type RemovableProtocolEntryProps = {
  entry: ProtocolFilter;
  onRemoveClick: (event: ButtonClickEvent) => void;
};

export const RemovableProtocolEntry = observer(({ entry, onRemoveClick }: RemovableProtocolEntryProps) => {
  const styles = useStyles(themedStyles);
  return (
    <div className={styles.plainContainer}>
      <div className={styles.iconContainer}>
        <div className={getProtocolFilterIcon(styles, entry)}/>
      </div>
      <div className={styles.detailContainer}>
        <div className={styles.detailText}>
          {getProtocolFilterLabel(entry)}
        </div>
        <button type='button' className={styles.bareButton} onClick={onRemoveClick}>
          <div className={styles.removeIcon}/>
        </button>
      </div>
    </div>
  );
});

export type SearchProtocolEntryProps = {
  data: ProtocolFilter;
  onSelect: () => void;
};

export const SearchProtocolEntry = observer(({ data: entry, onSelect }: SearchProtocolEntryProps) => {
  const styles = useStyles(themedStyles);
  return (
    <div className={styles.clickableContainer} onClick={onSelect}>
      <div className={styles.iconContainer}>
        <div className={getProtocolFilterIcon(styles, entry)}/>
      </div>
      <div className={styles.detailContainer}>
        <div className={styles.detailText}>
          {getProtocolFilterLabel(entry, true)}
        </div>
      </div>
    </div>
  );
});

export type PlainProtocolEntryProps = {
  entry: ProtocolFilter;
};

export const PlainProtocolFilter = observer(({ entry }: PlainProtocolEntryProps) => {
  const styles = useStyles(themedStyles);
  return (
    <div className={styles.plainContainer}>
      <div className={styles.iconContainer}>
        <div className={getProtocolFilterIcon(styles, entry)}/>
      </div>
      <div className={styles.detailContainer}>
        <div className={styles.detailText}>
          {getProtocolFilterLabel(entry)}
        </div>
      </div>
    </div>
  );
});
